import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store';
import { useUI } from '@/hooks/ui/useUI';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { incidentErrorTypeService, type IncidentErrorType } from '@/api/domains/catalog';
import { formatDateToYYYYMMDD } from '@/utils';
import { employeeIncidentService } from '@/api/domains/employeeIncident';
import type {
  IncidentItem,
  IncidentTotals,
  IncidentStatus,
} from '@/api/domains/employeeIncident';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import { colors } from '@/assets/styles/colors';
import { useMyIncidentsExport } from './useMyIncidentsExport';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';


export interface IncidentListFilters {
  from?: string;
  to?: string;
  errorTypeId: number | null;
  status: IncidentStatus;
}

export const useMyIncidentsList = () => {
  const { t: translate } = useTranslation('attendance');
  const { showNotification, showApiError, openSidePanel } = useUI();
  const employeeId = useSelector((state: RootState) => state.auth.user?.idEmpleado);

  const incidentErrorTypesCatalog = useCatalogLoader<IncidentErrorType>(
    (signal) => incidentErrorTypeService.getIncidentErrorTypes(signal),
    (type) => ({
      value: String(type.id),
      label: type.description,
    }),
    [],
  );

  const defaultFrom = useMemo(() => {
    const fromDate = new Date();
    fromDate.setMonth(fromDate.getMonth() - 1);
    return formatDateToYYYYMMDD(fromDate);
  }, []);

  const defaultTo = useMemo(() => formatDateToYYYYMMDD(new Date()), []);

  const [totals, setTotals] = useState<IncidentTotals>({
    pendientes: 0,
    aprobados: 0,
    rechazados: 0,
    error: 0,
    todos: 0,
  });

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<IncidentListFilters>,
      _signal: AbortSignal,
    ): Promise<PaginatedFetchResult<IncidentItem> | null> => {
      if (!employeeId) return null;

      const fromDateString = params.filters.from ?? defaultFrom;
      const toDateString = params.filters.to ?? defaultTo;

      const response = await employeeIncidentService.list({
        employeeId,
        startDate: fromDateString,
        endDate: toDateString,
        errorTypeId: params.filters.errorTypeId,
        status: params.filters.status,
        page: params.page,
        pageSize: params.pageSize,
        searchText: params.searchText || null,
        orderBy: params.sort?.key ?? null,
        orderDirection: params.sort?.direction ?? null,
      });

      if (!response.success || !response.data) {
        showApiError(response, translate('incidents.errors.loadFailed'));
        setTotals({ pendientes: 0, aprobados: 0, rechazados: 0, error: 0, todos: 0 });
        return null;
      }

      setTotals(response.data.totals);
      return { items: response.data.data, totalRecords: response.data.totalRecords };
    },
    [employeeId, defaultFrom, defaultTo, showApiError, translate],
  );

  const {
    rows: data,
    total: totalRecords,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading: isLoading,
    onSearch,
    onSortChange,
    filters,
    setFilters,
  } = usePaginatedList<IncidentItem, IncidentListFilters>({
    fetchFn,
    initialFilters: {
      from: defaultFrom,
      to: defaultTo,
      errorTypeId: null,
      status: 'todos',
    },
    initialSort: { key: 'createdAt', direction: 'DESC' },
    orderByMap: {
      id: 'createdAt',
      errorType: 'errorType',
      incidentDate: 'createdAt',
      checkInDate: 'checkInDate',
      status: 'status',
    },
    enabled: Boolean(employeeId),
  });

  // Export hook
  const { exportIncidents, isExporting } = useMyIncidentsExport({
    startDate: filters.from ?? defaultFrom,
    endDate: filters.to ?? defaultTo,
    errorTypeId: filters.errorTypeId,
    status: filters.status,
  });

  const handleFilterChange = useCallback((key: string, value: string | string[]) => {
    const parsedValue = key === 'errorTypeId' && typeof value === 'string'
      ? (value === '0' ? null : Number(value))
      : value;
    setFilters((previous) => ({ ...previous, [key]: parsedValue }));
  }, [setFilters]);

  // Filter options for dropdowns
  const errorTypeOptions = useMemo(() => [
    { value: '0', label: translate('incidents.filters.allErrorTypes') },
    ...incidentErrorTypesCatalog.options,
  ], [incidentErrorTypesCatalog.options, translate]);

  const statusOptions = useMemo(
    () => [
      { value: 'todos', label: translate('incidents.filters.allStatuses') },
      { value: 'pendiente', label: translate('incidents.status.pendiente') },
      { value: 'aprobado', label: translate('incidents.status.aprobado') },
      { value: 'rechazado', label: translate('incidents.status.rechazado') },
      { value: 'error', label: translate('incidents.status.error') },
    ],
    [translate],
  );

  const filterDefinitions = useMemo(
    () => [
      {
        key: 'status',
        label: translate('incidents.filters.status'),
        options: statusOptions,
        defaultValue: 'todos',
      },
      {
        key: 'errorTypeId',
        label: translate('incidents.filters.errorType'),
        options: errorTypeOptions,
        defaultValue: '0',
      },

    ],
    [translate, errorTypeOptions, statusOptions],
  );

  // Table columns
  const columns: TableColumn<IncidentItem>[] = useMemo(
    () => [
      {
        key: 'id',
        header: translate('incidents.table.columns.id'),
        sortable: true,
      },
      {
        key: 'errorType',
        header: translate('incidents.table.columns.errorType'),
        sortable: true,
      },
      {
        key: 'incidentDate',
        header: translate('incidents.table.columns.incidentDate'),
        sortable: true,
      },
      {
        key: 'checkInDate',
        header: translate('incidents.table.columns.checkInDate'),
        sortable: true,
      },
      {
        key: 'checkInTime',
        header: translate('incidents.table.columns.checkInTime'),
        sortable: false,
      },
      {
        key: 'location',
        header: translate('incidents.table.columns.location'),
        sortable: false,
      },
      {
        key: 'status',
        header: translate('incidents.table.columns.status'),
        sortable: true,
      },
    ],
    [translate],
  );

  const handleViewDetail = useCallback(
    async (row: IncidentItem) => {
      if (!employeeId) return;

      const response = await employeeIncidentService.getDetail(employeeId, row.id);

      if (!response.success || !response.data) {
        const detail = response.error?.validationErrors
          ?.map((validationError) => `${validationError.fieldName}: ${validationError.errorMessage}`)
          .join('; ');
        const message = [response.error?.description, detail]
          .filter(Boolean)
          .join(' — ');
        showNotification('error', message || translate('incidents.errors.loadDetailFailed'), 5000);
        return;
      }

      openSidePanel({
        component: 'IncidentDetailForm',
        title: translate('incidents.detail.title'),
        props: { incident: response.data },
      });
    },
    [employeeId, openSidePanel, showNotification, translate],
  );

  const actions: TableAction<IncidentItem>[] = useMemo(
    () => [
      {
        id: 'view-detail',
        label: translate('incidents.actions.viewDetail'),
        icon: 'eye' as const,
        color: colors.important.main,
        onClick: handleViewDetail,
      },
    ],
    [translate, handleViewDetail],
  );

  // Formatted filter values for FilterDropdown
  const formattedFilterValues = useMemo(
    () => ({
      errorTypeId: filters.errorTypeId !== null ? String(filters.errorTypeId) : '0',
      status: filters.status,
    }),
    [filters.errorTypeId, filters.status],
  );

  return {
    // Filters
    filters,
    setFilters,
    defaultFrom,
    defaultTo,
    filterDefinitions,
    formattedFilterValues,
    handleFilterChange,
    // Data
    data,
    totals,
    totalRecords,
    isLoading,
    // Pagination
    page,
    pageSize,
    setPage,
    setPageSize,
    // Search & Sort
    onSearch,
    onSortChange,
    // Table config
    columns,
    actions,
    // Export
    exportIncidents,
    isExporting,
    // i18n
    translate,
  };
};
